import { useState, useEffect, useRef } from "react";
import { Box, Typography, Button, Checkbox, Tooltip, FormControl, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, Radio, FormGroup, TextField, InputAdornment, Grid, Chip } from "@mui/material";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";

import ExpandMore from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import Snackbar from "@plannel/components/Snackbar";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import planConfigService from "@plannel/services/data/plan-config-service";
import "@plannel/scss/components/DataLoadValidation.scss"
import debug from "@plannel/utils/debug-util";
import { withTranslation } from "react-i18next";
import CodeUtil from "@plannel/pages/utils/code-util";
import { isEmpty, isEqual, isNil } from "lodash";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import MeasureNameSettings from "@plannel/components/MeasureNameSettings";
import { FS } from "@plannel/utils/font-util";

const ABC_XYZ_POLICY = "ABC_XYZ_POLICY";
const INV_TARGET_POLICY = "INV_TARGET_POLICY";
const INV_DAYS_POLICY = "INV_DAYS_POLICY";
const INV_TURNOVER_POLICY = "INV_TURNOVER_POLICY";
const INV_REPORT_POLICY = "INV_REPORT_POLICY";
const KIT = "KIT";
const FG = "FG";
const KIT_TO_FG = "KIT_TO_FG";
const KIT_TARGET_INIT = "KIT_TARGET_INIT";
const CALC_TYPE = "CALC_TYPE";
const QTY = "QTY";
const AMT = "AMT";
const COGS = "COGS";
const KIT_RATIO = "KIT_RATIO";
const FG_RATIO = "FG_RATIO";
const KIT_TO_FG_LABEL_DEMAND_SALES = "kitSalesDemandMergeFlg";
const KIT_TO_FG_LABEL_SALES = "kitSalesMergeFlg";
const KIT_TARGET_INIT_LABEL = "kitTargetInit";
const TARGET_RATIO = "targetRatio";
const { FG: FG_LABEL, KIT: KIT_LABEL } = CodeUtil.itemType
const MEASURE_DISPLAY_NAMES_CONFIG_CD = "MEASURE_DISPLAY_NAMES";

const { 
  IP_SETTINGS_CONFIG_CD, 
  TARGET_LOCATION, 
  SIM_TARGET_FILTER_POLICY,
  SIM_TARGET_FILTER_ENABLED,
  LOCATION_NODE_TREE,
  TARGET_ITEM_HRCHY,
  ITEM_HRCHY_NODE_TREE
} = CodeUtil.ipSettings;

const DEFAULT_IP_PLAN_CONFIG = {
  moduleCd: CodeUtil.module.IP,
  configCd: IP_SETTINGS_CONFIG_CD,
  activeFlg: true,
}

// 커스텀 스위치

const IpSettings = ({ t, viewName }) => {
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const ipPlanConfig = useRef(DEFAULT_IP_PLAN_CONFIG);
  const [filterValue, setFilterValue] = useState({ locations: null, category: null });
  const [abcXyzPolicy, setAbcXyzPolicy] = useState({ [FG]: true, [KIT]: true, [KIT_TO_FG]: false });
  const [invTargetPolicy, setInvTargetPolicy] = useState({ [FG]: true, [KIT]: true, [KIT_TO_FG]: false, [KIT_TARGET_INIT]: false, [KIT_RATIO]: 100, [FG_RATIO]: 100 });
  const [simTargetFilterPolicy, setSimTargetFilterPolicy] = useState({ [SIM_TARGET_FILTER_ENABLED]: false, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [], [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: [] });
  const [invDaysPolicy, setInvDaysPolicy] = useState({ [CALC_TYPE]: AMT });
  const [invTurnoverPolicy, setInvTurnoverPolicy] = useState({ [CALC_TYPE]: AMT });
  const [invReportPolicy, setInvReportPolicy] = useState({ [KIT_TO_FG]: false });

  const [measureDisplayNames, setMeasureDisplayNames] = useState({});
  const measureDisplayNameConfig = useRef({ moduleCd: CodeUtil.module.IP, configCd: MEASURE_DISPLAY_NAMES_CONFIG_CD, activeFlg: true });
  const [ipMeasureList, setIpMeasureList] = useState([]);

  // QTY/AMT/COGS 계산방식 세그먼트 옵션
  const calcTypeOptions = [
    { value: QTY, label: t(QTY.toLowerCase()) },
    { value: AMT, label: t(AMT.toLowerCase()) },
    { value: COGS, label: t(COGS.toLowerCase()) },
  ];

  const retrieveMeasureDisplayNames = () => {
    planConfigService.getMeasureDisplayNames(CodeUtil.module.IP)
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          measureDisplayNameConfig.current = data;
        }
        setMeasureDisplayNames(data?.objVal ?? {});
      })
      .catch(e => {
        debug(e);
      });
  };

  const handleSaveMeasureDisplayNames = () => {
    planConfigService.upsert([{ ...measureDisplayNameConfig.current, objVal: measureDisplayNames }])
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          measureDisplayNameConfig.current = data[0];
        }
        retrieveMeasureDisplayNames();
        setSnackInfo({ open: true, severity: "success", content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: "error", content: t("MSG_SaveFailed") });
      });
  };

  const onChangeCheckBoxHandler = (e, targetPolicy, targetType) => {
    const targetName = e.target.name;
    const checkedValue = e.target?.checked;
  
    let changeValue = { [targetName]: checkedValue };

    switch (targetPolicy) {
      case ABC_XYZ_POLICY: {
        if (isEqual(targetType, KIT) && checkedValue) {
          /* KIT 체크시 KIT_TO_FG 체크 해제 (분석시 더블링) */
          changeValue = { ...changeValue, [KIT_TO_FG]: false };
        } else if (isEqual(targetType, FG) && !checkedValue) {
          /* FG 체크 해제시 KIT_TO_FG 체크 해제 */
          changeValue = { ...changeValue, [KIT_TO_FG]: false };
        }

        setAbcXyzPolicy({ ...abcXyzPolicy, ...changeValue });
        break;
      }
      case INV_TARGET_POLICY: {
        /* [FG, KIT, KIT_TO_FG] 체크 조건 */
        if (isEqual(targetType, FG)) {
          if (checkedValue) {
            /* FG가 체크시 KIT_TARGET_INIT 체크 해제 */
            changeValue = { ...changeValue, [KIT_TARGET_INIT]: false };
          } else {
            /* FG 체크 해제시 KIT_TO_FG 체크 해제 */
            changeValue = { ...changeValue, [KIT_TARGET_INIT]: false, [KIT_TO_FG]: false, [KIT_RATIO]: 100, [FG_RATIO]: 100, [KIT_RATIO]: 100 };
          }

          /* FG, KIT 모두 해제시 KIT_TO_FG 해제 및 Ratio 100% 초기화 */
          if (!checkedValue && !invTargetPolicy?.[KIT]) {
            changeValue = { ...changeValue, [KIT_TO_FG]: false, [KIT_RATIO]: 100, [FG_RATIO]: 100 };  
          }
        } else if (isEqual(targetType, KIT)) {
          if (checkedValue) {
            /* KIT 체크시 KIT_TARGET_INIT 체크 해제 */
            changeValue = { ...changeValue, [KIT_TARGET_INIT]: false };
          }

          /* FG, KIT 모두 해제시 KIT_TO_FG 해제 및 Ratio 100% 초기화 */
          if (!checkedValue && !invTargetPolicy?.[FG]) {
            changeValue = { ...changeValue, [KIT_TO_FG]: false, [KIT_RATIO]: 100, [FG_RATIO]: 100 };
          }

        } else if (isEqual(targetType, KIT_TO_FG)) {
          /* KIT_TO_FG 체크시 FG:100%, KIT:0% 기본설정 */
          if (checkedValue) {
            changeValue = { ...changeValue, [KIT_RATIO]: 0, [FG_RATIO]: 100 };
          } else {
            /* KIT_TO_FG 체크 해제시 FG, KIT Ratio 100% 초기화 */
            changeValue = { ...changeValue, [KIT_RATIO]: 100, [FG_RATIO]: 100 };
          }
        }

        setInvTargetPolicy({ ...invTargetPolicy, ...changeValue });
        break;
      }
      case INV_REPORT_POLICY: {
        setInvReportPolicy({ ...invReportPolicy, ...changeValue});
        break;
      }
      default:
        break;
    }
  }

  const onChangeRadioHandler = (e, targetPolicy) => {
    switch (targetPolicy) {
      case INV_DAYS_POLICY:
        setInvDaysPolicy({ ...invDaysPolicy, [e.target.name]: e.target.value });
        break;
      case INV_TURNOVER_POLICY:
        setInvTurnoverPolicy({ ...invTurnoverPolicy, [e.target.name]: e.target.value });
        break;
      default:
        break;
    }
  }

  const onChangeRatioHandler = (e) => {
    const targetName = e.target.name;
    const value = e.target.value;

    if (value < 0) {
      setInvTargetPolicy({ ...invTargetPolicy, [targetName]: 0 });
    } else if (value > 100) {
      setInvTargetPolicy({ ...invTargetPolicy, [targetName]: 100 });
    } else {
      setInvTargetPolicy({ ...invTargetPolicy, [targetName]: Number(value) });
    }
  }

  const checkInvTargetPolicy = (targetPolicy) => {
    if (isNil(targetPolicy?.[FG])) {
      targetPolicy[FG] = true;
    }

    if (isNil(targetPolicy?.[KIT])) {
      targetPolicy[KIT] = true;
    }

    if (isNil(targetPolicy?.[KIT_TARGET_INIT])) {
      targetPolicy[KIT_TARGET_INIT] = false;
    }

    if (isNil(targetPolicy?.[KIT_TO_FG])) {
      targetPolicy[KIT_TO_FG] = false;
    }

    if (isNil(targetPolicy?.[KIT_RATIO])) {
      if (!isNil(targetPolicy?.[KIT_TO_FG] && targetPolicy?.[KIT_TO_FG])) {
        targetPolicy[KIT_RATIO] = 0;
      } else {
        targetPolicy[KIT_RATIO] = 100;
      }
    }

    if (isNil(targetPolicy?.[FG_RATIO])) {
      targetPolicy[FG_RATIO] = 100;
    }

    return targetPolicy;
  }

  const setPolicy = (objVal) => {
    if (!isEmpty(objVal)) {
      const {
        ABC_XYZ_POLICY,
        INV_TARGET_POLICY,
        INV_DAYS_POLICY,
        INV_TURNOVER_POLICY,
        INV_REPORT_POLICY,
        SIM_TARGET_FILTER_POLICY,
      } = objVal;
      setAbcXyzPolicy(ABC_XYZ_POLICY);
      setInvTargetPolicy(checkInvTargetPolicy(INV_TARGET_POLICY));
      setInvDaysPolicy(INV_DAYS_POLICY);
      setInvTurnoverPolicy(INV_TURNOVER_POLICY);
      setInvReportPolicy(INV_REPORT_POLICY);
      setSimTargetFilterPolicy(SIM_TARGET_FILTER_POLICY);

      /* Filter Value Setting [LOCATION_NODE_TREE, ITEM_HRCHY_NODE_TREE] 
      * filterValue: { locations: [], category: [] } Node Tree Data 형식이므로 셋팅.
      */

      const locationTargetTreeNode = SIM_TARGET_FILTER_POLICY?.[LOCATION_NODE_TREE] || [];
      const itemHrchyTargetTreeNode = SIM_TARGET_FILTER_POLICY?.[ITEM_HRCHY_NODE_TREE] || [];

      setFilterValue({ locations: locationTargetTreeNode, category: itemHrchyTargetTreeNode });
    }
  }

  const retrieveIpConfig = () => {
    planConfigService.getIpPlanConfig()
      .then((response) => {
        const { data } = response;
        if (!isEmpty(data)) {
          ipPlanConfig.current = data;
          const { objVal } = ipPlanConfig.current;

          setPolicy(objVal);
        }
      })
      .catch((error) => {
        debug(error);
      })
  }

  const saveDataValidation = () => {
    if (!abcXyzPolicy[FG] && !abcXyzPolicy[KIT]) {
      setSnackInfo({ open: true, severity: "error", content: t("MSG_EssentialAbcXyzPolicy") });
      return false;
    }

    if (!invTargetPolicy[FG] && !invTargetPolicy[KIT]) {
      setSnackInfo({ open: true, severity: "error", content: t("MSG_EssentialInvTargetPolicy") });
      return false;
    }

    const simTargetFilterEnabled = simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED] || false;
    /* SImulation Target 조건을 해제시 저장할 데이터 초기화 */
    if (!simTargetFilterEnabled && !isNil(ipPlanConfig.current.objVal[SIM_TARGET_FILTER_POLICY])) {
      ipPlanConfig.current.objVal[SIM_TARGET_FILTER_POLICY][TARGET_LOCATION] = [];
      ipPlanConfig.current.objVal[SIM_TARGET_FILTER_POLICY][LOCATION_NODE_TREE] = [];
      ipPlanConfig.current.objVal[SIM_TARGET_FILTER_POLICY][TARGET_ITEM_HRCHY] = [];
      ipPlanConfig.current.objVal[SIM_TARGET_FILTER_POLICY][ITEM_HRCHY_NODE_TREE] = [];
    }

    const targetLocList = simTargetFilterPolicy?.[TARGET_LOCATION] || [];
    const targetLocTree = simTargetFilterPolicy?.[LOCATION_NODE_TREE] || [];
    const targetItemHrchyList = simTargetFilterPolicy?.[TARGET_ITEM_HRCHY] || [];
    const targetItemHrchyTree = simTargetFilterPolicy?.[ITEM_HRCHY_NODE_TREE] || [];

    if (simTargetFilterEnabled && !isNil(ipPlanConfig.current.objVal[SIM_TARGET_FILTER_POLICY])) {
      if (isEmpty(targetLocList) || isEmpty(targetLocTree)) {
        setSnackInfo({ open: true, severity: "error", content: t("MSG_NoSelectedSimTargetLocation") });
        return false;
      }

      if (isEmpty(targetItemHrchyList) || isEmpty(targetItemHrchyTree)) {
        setSnackInfo({ open: true, severity: "error", content: t("MSG_NoSelectedSimTargetItemHrchy") });
        return false;
      }
    }

    return true;
  }

  const handleSave = () => {
    if(saveDataValidation()) {
      planConfigService.upsert([{ ...ipPlanConfig.current }])
      .then((response) => {
        const { data } = response;
        if (response?.status === 200 && !isEmpty(data)) {
          const IpSettingConfig = data[0];
          ipPlanConfig.current = IpSettingConfig;
          const { objVal } = ipPlanConfig.current;
  
          setPolicy(objVal);
          setSnackInfo({ open: true, severity: "success", content: t("MSG_SaveSuccess") });
        } else {
          setSnackInfo({ open: true, severity: "error", content: t("MSG_SaveFailed") });
        }
      }).catch((error) => {
        debug(error);
      }).finally(() => {
        handleSaveMeasureDisplayNames();
      })
    } 
  }
  
  useEffect(() => {
    retrieveIpConfig();
    retrieveMeasureDisplayNames();
    // ipMeasure 리스트 생성
    const measures = Object.values(CodeUtil.ipMeasure);
    setIpMeasureList(measures);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const objVal = {
      [ABC_XYZ_POLICY]: abcXyzPolicy,
      [INV_TARGET_POLICY]: invTargetPolicy,
      [INV_DAYS_POLICY]: invDaysPolicy,
      [INV_TURNOVER_POLICY]: invTurnoverPolicy,
      [INV_REPORT_POLICY]: invReportPolicy,
      [SIM_TARGET_FILTER_POLICY]: simTargetFilterPolicy,
    };

    if (!isEmpty(ipPlanConfig.current)) {
      ipPlanConfig.current.objVal = objVal;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzPolicy, invTargetPolicy, invDaysPolicy, invTurnoverPolicy, invReportPolicy, simTargetFilterPolicy]);

  return (
    <Box id={"data-load-validation"} sx={{ mt: 1, height: "100%", width: "100%" }}>
      <Box sx={{ width: "100%", height: "calc(100% - 8px)", overflow: "auto" }}>

        {/* ABC-XYZ 대상 품목 */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("abcXyzTargetType")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main">
                <Box display={"inline-flex"} sx={{ width: "100%" }}>
                  <CheckBoxCompo
                    checkboxLabel={t(KIT_LABEL.name)}
                    checkboxName={KIT}
                    checkboxValue={abcXyzPolicy?.[KIT]}
                    targetPolicy={ABC_XYZ_POLICY}
                    onChangeHanlder={(e, targetPolicy) => onChangeCheckBoxHandler(e, targetPolicy, KIT)}
                  />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <FormGroup row>
                    <CheckBoxCompo
                      checkboxLabel={t(FG_LABEL.name)}
                      checkboxName={FG}
                      checkboxValue={abcXyzPolicy?.[FG]}
                      targetPolicy={ABC_XYZ_POLICY}
                      onChangeHanlder={(e, targetPolicy) => onChangeCheckBoxHandler(e, targetPolicy, FG)}
                    />
                    <CheckBoxCompo
                      checkboxLabel={t(KIT_TO_FG_LABEL_DEMAND_SALES)}
                      checkboxName={KIT_TO_FG}
                      checkboxValue={abcXyzPolicy?.[KIT_TO_FG]}
                      targetPolicy={ABC_XYZ_POLICY}
                      onChangeHanlder={onChangeCheckBoxHandler}
                      disabled={abcXyzPolicy?.[KIT] || !abcXyzPolicy?.[FG]}
                    />
                  </FormGroup>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Target Inventory 대상 품목 */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("targetStockTargetType")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main">
                <Box display={"inline-flex"} sx={{ width: "100%", mb: 1 }}>
                  <FormGroup row>
                    <CheckBoxCompo // using TextField for KIT_RATIO
                      t={t}
                      invTargetPolicy={invTargetPolicy}
                      onChangeRatioHandler={onChangeRatioHandler}
                      textFieldLabel={t(TARGET_RATIO)}
                      textFieldName={KIT_RATIO}
                      checkboxLabel={t(KIT_LABEL.name)}
                      checkboxName={KIT}
                      checkboxValue={invTargetPolicy?.[KIT]}
                      targetPolicy={INV_TARGET_POLICY}
                      onChangeHanlder={(e, targetPolicy) => onChangeCheckBoxHandler(e, targetPolicy, KIT)}
                    />
                    <CheckBoxCompo
                      checkboxLabel={t(KIT_TARGET_INIT_LABEL)}
                      checkboxName={KIT_TARGET_INIT}
                      checkboxValue={invTargetPolicy?.[KIT_TARGET_INIT]}
                      targetPolicy={INV_TARGET_POLICY}
                      onChangeHanlder={onChangeCheckBoxHandler}
                      disabled={invTargetPolicy?.[KIT] || !invTargetPolicy?.[FG]}
                    />
                  </FormGroup>
                </Box>
                <Box sx={{ width: "100%", mb: 1 }}>
                  <FormGroup row>
                    <CheckBoxCompo // using TextField for FG_RATIO
                      t={t}
                      invTargetPolicy={invTargetPolicy}
                      onChangeRatioHandler={onChangeRatioHandler}
                      textFieldLabel={t(TARGET_RATIO)}
                      textFieldName={FG_RATIO}
                      checkboxLabel={t(FG_LABEL.name)}
                      checkboxName={FG}
                      checkboxValue={invTargetPolicy?.[FG]}
                      targetPolicy={INV_TARGET_POLICY}
                      onChangeHanlder={(e, targetPolicy) => onChangeCheckBoxHandler(e, targetPolicy, FG)}
                    />
                    <CheckBoxCompo
                      checkboxLabel={t(KIT_TO_FG_LABEL_DEMAND_SALES)}
                      checkboxName={KIT_TO_FG}
                      checkboxValue={invTargetPolicy?.[KIT_TO_FG]}
                      targetPolicy={INV_TARGET_POLICY}
                      onChangeHanlder={(e, targetPolicy) => onChangeCheckBoxHandler(e, targetPolicy, KIT_TO_FG)}
                      disabled={!invTargetPolicy?.[FG]}
                    />
                  </FormGroup>
                </Box>
                <IpTargetLocationItemHrchyFullComponent
                  t={t}
                  viewName={viewName}
                  simTargetFilterPolicy={simTargetFilterPolicy}
                  setSimTargetFilterPolicy={setSimTargetFilterPolicy}
                  contentStyles={{ display: "flex" }}
                  filterValue={filterValue}
                  setFilterValue={setFilterValue}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Report 계산방식및 KIT Demand 설정 */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("invReport")}</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box className="content-main">

                {/* Inventory Days 계산방식및 KIT Demand 설정 */}
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ alignContent: "center" }}>
                    <Typography variant="h6">{t("invDaysCalcFormula")}</Typography>
                  </Box>
                  <SegmentedControl
                    sx={{ mt: 0.5 }}
                    value={invDaysPolicy?.[CALC_TYPE]}
                    onChange={(v) => onChangeRadioHandler({ target: { name: CALC_TYPE, value: v } }, INV_DAYS_POLICY)}
                    options={calcTypeOptions}
                  />
                </Box>

                {/* Inventory Turnover 계산방식및 KIT Demand 설정 */}
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ alignContent: "center", fontWeight: "bold" }}> 
                    <Typography variant="h6">{t("invTurnoverCalcFormula")}</Typography>
                  </Box>
                  <SegmentedControl
                    sx={{ mt: 0.5 }}
                    value={invTurnoverPolicy?.[CALC_TYPE]}
                    onChange={(v) => onChangeRadioHandler({ target: { name: CALC_TYPE, value: v } }, INV_TURNOVER_POLICY)}
                    options={calcTypeOptions}
                  />
                </Box>
              </Box>
            </AccordionDetails>

            {/* Inventory Report KIT Demand To FG 설정 */}
            <AccordionDetails>
              <Box className="content-main">
                <Box display={"inline-flex"} sx={{ width: "100%" }}>
                  <FormControlLabel
                    control={
                      <StyledSwitch
                        name={KIT_TO_FG}
                        checked={invReportPolicy?.[KIT_TO_FG] || false}
                        onChange={(e) => onChangeCheckBoxHandler(e, INV_REPORT_POLICY)}
                        sx={{ mr: 2 }}
                      />
                    }
                    label={t(KIT_TO_FG_LABEL_SALES)}
                  />
                </Box>
              </Box>
            </AccordionDetails>

          </Accordion>
        </Box>
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="measure-panel-content" id="measure-panel-header">
              <Typography className="content-title">{t("measureSetting")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main" sx={{ width: "100%" }}>
                <MeasureNameSettings
                  measures={ipMeasureList}
                  displayNames={measureDisplayNames}
                  onChange={setMeasureDisplayNames}
                  sx={{ mb: 1.5 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Button sx={{ width: "160px", height: "40px" }}
          className="content-button"
          color="primary"
          variant="contained"
          component="span"
          startIcon={<SaveIcon />}
          onClick={handleSave}>
          {t("save")}
        </Button>
      </Box>
        <Snackbar
          open={snackInfo.open}
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          content={snackInfo.content}
        />
    </Box >

  );
};

const CheckBoxCompo = ({
  invTargetPolicy, onChangeRatioHandler, textFieldLabel, textFieldName,
  checkboxLabel, checkboxName, checkboxValue, targetPolicy, tooltip,
  onChangeHanlder, isRadio = false, disabled, style
}) => {

  const checkBox = () => {
    return <Checkbox name={checkboxName} checked={checkboxValue} onChange={(e) => { onChangeHanlder(e, targetPolicy) }} sx={{ m: 0 }} />
  }

  const radio = () => {
    return <Radio name={checkboxName} value={checkboxValue} sx={{ m: 0 }} />
  }

  if (!isEmpty(invTargetPolicy)) {
    return <Tooltip title={tooltip}>
      <FormControl sx={{ minWidth: "320px", width: "auto", mt: 0.5, mr: 1, alignContent: "center", ...style }} disabled={disabled}>
        <FormGroup row sx={{justifyContent: "space-between"}}>
          <Box minWidth={"140px"}>
            <FormControlLabel
              control={isRadio ? radio() : checkBox()}
              label={checkboxLabel}
            />
          </Box>
          <Box sx={{ minWidth: "130px", width: "130px", alignContent: "center", mr: 2 }}>
            <TextField
              size="small"
              label={textFieldLabel}
              value={!isNil(invTargetPolicy?.[textFieldName]) ? invTargetPolicy?.[textFieldName] : 0}
              name={textFieldName}
              type="number"
              InputProps={{ endAdornment: <InputAdornment position="end" >%</InputAdornment> }}
              inputProps={{ inputMode: "decimal", pattern: "[0-9]", min: 0, max: 100 }}
              sx={{ textAlign: "center" }}
              onChange={onChangeRatioHandler}
              disabled={!invTargetPolicy?.[KIT_TO_FG]}
            />
          </Box>
        </FormGroup>
      </FormControl>
    </Tooltip>
  }

  return (
    <Tooltip title={tooltip}>
      <FormControl sx={{ minWidth: "140px", width: "auto", mt: 0.5, alignContent: "center", ...style }} disabled={disabled}>
        <FormControlLabel
          control={isRadio ? radio() : checkBox()}
          label={checkboxLabel}
        />
      </FormControl>
    </Tooltip>
  )
}

const IpTargetLocationItemHrchyFullComponent = ({ t, viewName, simTargetFilterPolicy, setSimTargetFilterPolicy, contentStyles, filterValue, setFilterValue, checkBoxDisabled }) => {
  const selectedSimTargetFilter = simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED] || false;

  return (
    <Box width={"100%"}>
      <IpTargetLocationItemHrchyEnabledComponent
        simTargetFilterPolicy={simTargetFilterPolicy}
        setSimTargetFilterPolicy={setSimTargetFilterPolicy}
        disabled={checkBoxDisabled}
        label={t("advancedSettings")}
        controlType="switch"
      />
      {selectedSimTargetFilter &&
        <IpTargetLocationItemHrchyComponentContent
          viewName={viewName}
          simTargetFilterPolicy={simTargetFilterPolicy}
          setSimTargetFilterPolicy={setSimTargetFilterPolicy}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          contentStyles={contentStyles}
        />
      }
    </Box>
  )
}

const IpTargetLocationItemHrchyEnabledComponent = ({
  simTargetFilterPolicy,
  setSimTargetFilterPolicy,
  label,
  disabled,
  sx,
  switchSx,
  controlType = "switch",
  labelPlacement = "end",
  checkboxSx,
}) => {
  const selectedSimTargetFilter = simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED] || false;

  const onChangeHandler = (e) => {
    const checkedValue = e.target.checked;
    const changeValue = { [SIM_TARGET_FILTER_ENABLED]: checkedValue };
    
    setSimTargetFilterPolicy({ ...simTargetFilterPolicy, ...changeValue });
  }

  if (controlType === "checkbox") {
    return (
      <CheckBoxCompo
        checkboxLabel={label}
        checkboxName={SIM_TARGET_FILTER_ENABLED}
        checkboxValue={selectedSimTargetFilter}
        onChangeHanlder={onChangeHandler}
        disabled={disabled}
        style={{ mt: 0, ...checkboxSx, ...sx }}
      />
    )
  }

  return (
    <FormControlLabel
      control={
        <StyledSwitch
          size="small"
          checked={selectedSimTargetFilter}
          onChange={onChangeHandler}
          disabled={disabled}
          sx={{ mr: 2, ...switchSx }}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
      sx={{ mb: 2, ...sx }}
    />
  )
}

const IpTargetLocationItemHrchyComponentContent = ({ viewName, simTargetFilterPolicy, setSimTargetFilterPolicy, filterValue, setFilterValue, contentStyles, filterStyles, disabled }) => {  
  return (
    <Box sx={{...contentStyles}} >
      <LocationTargetComponent
        viewName={viewName}
        simTargetFilterPolicy={simTargetFilterPolicy}
        setSimTargetFilterPolicy={setSimTargetFilterPolicy}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        filterStyles={{ ...filterStyles }}
        disabled={disabled}
      />
      <ItemHrchyTargetComponent
        viewName={viewName}
        simTargetFilterPolicy={simTargetFilterPolicy}
        setSimTargetFilterPolicy={setSimTargetFilterPolicy}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        filterStyles={{ ...filterStyles }}
        disabled={disabled}
      />
    </Box>
  )
}

const LocationTargetComponent = ({ viewName, setSimTargetFilterPolicy, setFilterValue, filterValue, filterStyles, disabled }) => {
  const selectedLocations = filterValue?.locations || [];

  const removeHandler = (index) => {
    setFilterValue((prev) => ({ ...prev, locations: prev?.locations?.filter((_, i) => i !== index) }));
  };

  useEffect(() => {
    if (isNil(selectedLocations) && isEmpty(selectedLocations)) {
      setSimTargetFilterPolicy((prev) => ({ ...prev, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [] }));
    } else {

      const targetLocIds = selectedLocations?.map((loc) => loc.id);
      setSimTargetFilterPolicy((prev) => ({ ...prev, [TARGET_LOCATION]: targetLocIds, [LOCATION_NODE_TREE]: selectedLocations }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocations])

  return (
    <Box width={"300px"} sx={{ mr: 2, mb: 1 }}>
      <Grid item xs={6}>
        <LocationTreeFilter
          viewName={viewName}
          onChangeFilter={setFilterValue}
          filterValue={selectedLocations?.map(l => l?.locCd)}
          disabled={disabled}
          styles={{ width: "300px" }}
        />
        <Box
          sx={{
            mt: 1,
            minHeight: 180,
            maxHeight: 180,
            overflowY: "auto",
            scrollbarGutter: "stable",
            borderRadius: 2,
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            p: 0.5,
            ...filterStyles
          }}
        >
          {selectedLocations.map((locationObj, idx) => (
            <Chip
              key={locationObj?.locCd}
              label={locationObj?.name}
              disabled={disabled}
              onDelete={() => removeHandler(idx)}
              sx={{
                height: 24,              // small(24px) 보다 더 작게
                "& .MuiChip-label": {
                  fontSize: FS.SUB,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 240,         // 칩이 너무 길어지지 않도록 (필요시 조절)
                },
                "& .MuiChip-deleteIcon": { fontSize: FS.H1 }, // 아이콘도 살짝 축소
                mr: 1, mb: 1, // 칩 간격 조절
              }}
            />
          ))}
        </Box>
      </Grid>
    </Box>
  )
}

const ItemHrchyTargetComponent = ({ viewName, setSimTargetFilterPolicy, setFilterValue, filterValue, filterStyles, disabled, label }) => {
  const selectedItemHrchy = filterValue?.category || [];
  const [customValue, setCustomValue] = useState(selectedItemHrchy);

  const removeHandlerForItemHrchy = (idx) => {
    const categoryLeafFilterNodes = filterValue?.category?.filter((item) => item?.leafFlg);

    /* leaf만 선택가능하도록 구성되어있기때문에 leaf에서 idx remove logic */
    const newCategoryFilterValue = categoryLeafFilterNodes.filter((_, i) => i !== idx);
    setCustomValue(newCategoryFilterValue);

    setFilterValue({...filterValue, category: newCategoryFilterValue});
  }

  useEffect(() => {
    if (isNil(selectedItemHrchy) || isEmpty(selectedItemHrchy)) {
      setSimTargetFilterPolicy((prev) => ({ ...prev, [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: [] }));
    } else {
      const targetItemHrchyIds = selectedItemHrchy?.filter((itemHrchy) => itemHrchy.leafFlg)?.map((item) => item.id);

      setSimTargetFilterPolicy((prev) => ({ ...prev, [TARGET_ITEM_HRCHY]: targetItemHrchyIds, [ITEM_HRCHY_NODE_TREE]: selectedItemHrchy }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemHrchy])

  return (
    <Box width={"300px"} sx={{ mr: 2, mb: 1 }}>
      <Grid item xs={6}>
        <ItemHrchyTreeFilter
          viewName={viewName}
          onChangeFilter={setFilterValue}
          enableMultiSelect
          selectLeafs
          label={label}
          styles={{ width: "300px" }}
          disabled={disabled}
          customValue={customValue}
        />
        <Box
          sx={{
            mt: 1,
            minHeight: 180,
            maxHeight: 180,
            overflow: "auto",
            scrollbarGutter: 'stable',
            borderRadius: 2,
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            p: 0.5,
            ...filterStyles
          }}
        >
          {selectedItemHrchy?.filter((data) => data?.leafFlg).map((categoryObj, idx) => (
            <Chip
              key={categoryObj?.hrchyCd}
              label={categoryObj?.name}
              disabled={disabled}
              onDelete={() => removeHandlerForItemHrchy(idx)}
              sx={{
                height: 24,              // small(24px) 보다 더 작게
                "& .MuiChip-label": {
                  fontSize: FS.SUB,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 240,         // 칩이 너무 길어지지 않도록 (필요시 조절)
                },
                "& .MuiChip-deleteIcon": { fontSize: FS.H1 }, // 아이콘도 살짝 축소
                mr: 1, mb: 1, // 칩 간격 조절
              }}
            />
          ))}
        </Box>

      </Grid>
    </Box>
  )
}

export default withTranslation()(IpSettings);
export { 
  CheckBoxCompo, 
  IpTargetLocationItemHrchyFullComponent, 
  IpTargetLocationItemHrchyEnabledComponent, 
  IpTargetLocationItemHrchyComponentContent, 
  LocationTargetComponent, 
  ItemHrchyTargetComponent 
};
